import { useEffect, useState } from "react";
import { Archive, Download, FileText, FileSpreadsheet, Scale, Calendar } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { backupSummary, backupZipUrl } from "@/lib/api";

export default function BackupDialog({ open, onOpenChange }) {
  const [summary, setSummary] = useState(null);
  const [year, setYear] = useState("all");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open) return;
    backupSummary().then(setSummary).catch(() => toast.error("Özet yüklenemedi"));
  }, [open]);

  const startDownload = () => {
    setDownloading(true);
    const url = backupZipUrl(year === "all" ? null : year);
    // Trigger via hidden link
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noreferrer";
    a.click();
    toast.success("Yedekleme başlatıldı — tarayıcı indirmelerini kontrol edin");
    setTimeout(() => setDownloading(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-xl" data-testid="backup-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-amber-500 flex items-center gap-2">
            <Archive className="w-5 h-5" /> Veri Yedekleme (ZIP Arşivi)
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <p className="text-sm text-slate-300">
            Tüm davalarınızı, belgelerinizi, tahsilatlarınızı ve duruşmalarınızı tek bir ZIP dosyasında indirin.
          </p>

          {summary && (
            <div className="grid grid-cols-4 gap-2" data-testid="backup-summary">
              <SumCard icon={<Scale className="w-3.5 h-3.5" />} label="Dava" value={summary.case_count} />
              <SumCard icon={<FileText className="w-3.5 h-3.5" />} label="Belge" value={summary.document_count} />
              <SumCard icon={<FileSpreadsheet className="w-3.5 h-3.5" />} label="Tahsilat" value={summary.payment_count} />
              <SumCard icon={<Calendar className="w-3.5 h-3.5" />} label="Duruşma" value={summary.hearing_count} />
            </div>
          )}

          <div>
            <Label className="text-xs text-slate-400">Yıl Filtresi</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="bg-slate-800 border-slate-700 mt-1" data-testid="backup-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="all">Tüm Yıllar</SelectItem>
                {summary?.years?.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[11px] text-slate-500 mt-1 font-mono">
              {year === "all" ? "Tüm veriler" : `Sadece ${year} yılında oluşturulan davalar`}
            </div>
          </div>

          <div className="border border-slate-800 rounded p-4 bg-slate-950/40">
            <div className="text-[11px] font-mono uppercase tracking-widest text-slate-400 mb-2">ARŞİV İÇERİĞİ</div>
            <ul className="text-sm text-slate-300 space-y-1.5">
              <li className="flex items-center gap-2"><Badge variant="outline" className="border-slate-700 text-slate-400 font-mono">.csv</Badge> cases, payments, hearings, documents meta</li>
              <li className="flex items-center gap-2"><Badge variant="outline" className="border-slate-700 text-slate-400 font-mono">files</Badge> tüm belgeler (udf, pdf, docx, xlsx, görseller)</li>
              <li className="flex items-center gap-2"><Badge variant="outline" className="border-slate-700 text-slate-400 font-mono">.json</Badge> MANIFEST + README</li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>İptal</Button>
          <Button onClick={startDownload} disabled={downloading || !summary || summary.case_count === 0}
            className="bg-amber-600 hover:bg-amber-700 text-slate-950 font-semibold"
            data-testid="backup-download-btn">
            <Download className="w-4 h-4 mr-2" />
            {downloading ? "Hazırlanıyor..." : "ZIP İndir"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SumCard({ icon, label, value }) {
  return (
    <div className="bg-slate-800/60 border border-slate-700 rounded p-2.5 text-center">
      <div className="flex items-center justify-center gap-1 text-[10px] font-mono uppercase tracking-wider text-slate-500">
        {icon}{label}
      </div>
      <div className="text-lg font-serif text-amber-500 mt-0.5">{value}</div>
    </div>
  );
}
